import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import { Button, FlexColumn, FlexRow, IconButton, LocaleNumberField, SingleSelect } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { grey, white } from "designSystem/styles/color";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import BaseCompositionMaterialList, { BaseCompositionMaterialListProps } from "./BaseCompositionMaterialList";
import { useBaseCompositionStore } from "./BaseCompositionStore";
import { CompositionPlacement } from "./CompositionPlacement";

interface BaseCompositionBOMMaterialListProps extends BaseCompositionMaterialListProps {
	/** Parent placement to be used here */
	parent: CompositionPlacement;
}

const BaseCompositionBOMMaterialList: FunctionComponent<BaseCompositionBOMMaterialListProps> = observer(({
	parent,
	compositionMaterialsHaveDummy,
}) => {
	const {
		currentSelectedBOM,
		disableBOMMaterialEdit,
		fromStyleBom,
		getSelectActions,
		shouldInputAutoFocus,
	} = useBaseCompositionStore();

	const {
		addBOMMaterial,
		getBomMaterialItemsForDropdown,
		getCurrentMaterialBOMDropdownValue,
		materialBOMs,
		onClickBOMMaterialGoToDialog,
		percentageFieldHighlightRed,
		removeBOMMaterial,
	} = parent;

	const loc = useLocale();

	return (
		<FlexColumn
			alignItems="flex-start"
			gap={2}
			paddingLeft={7}
			width="100%"
		>
			{materialBOMs.map((materialBOM, index) => {
				const {
					addBaseCompositionMaterial,
					baseCompositionMaterials,
					compositionMaterialsHaveDummy,
					getShowErrorBOMMaterial,
					key,
					percentageFixed,
					restrictedPercentage,
					setMaterialPercentage,
					setUrlFromItem,
				} = materialBOM;

				const currentBOMs = getBomMaterialItemsForDropdown(index);

				return (
					<FlexColumn
						alignItems="flex-start"
						bgcolor={white[50]}
						border={1}
						borderColor={grey[300]}
						borderRadius={1}
						data-csi-automation="BOMMaterial"
						gap={2}
						key={key}
						paddingLeft={3}
						paddingRight={2}
						paddingY={2}
						width="100%"
					>
						<FlexRow
							alignItems="center"
							data-csi-automation="BOMMaterialAggregated"
							gap={2}
							width="100%"
						>
							<SingleSelect
								dataCsiAutomation="Select-Bom-Material"
								disableClearable
								disabled={disableBOMMaterialEdit}
								fullWidth
								inputAutoFocus={shouldInputAutoFocus}
								onChange={setUrlFromItem}
								openOnFocus={shouldInputAutoFocus}
								options={currentBOMs}
								placeHolderRes="BOMMaterialNoSelectedItems"
								selectActions={currentBOMs.length > 0 ? getSelectActions(onClickBOMMaterialGoToDialog) : null}
								value={getCurrentMaterialBOMDropdownValue(materialBOM)}
							/>
							<LocaleNumberField
								InputProps={{
									endAdornment:
										<InputAdornment position="end">
											{loc.resource("PercentageSign")}
										</InputAdornment>,
								}}
								dataCsiAutomation="textfield-BOM-Material-Percentage"
								defaultValue={percentageFixed}
								disabled={disableBOMMaterialEdit}
								error={getShowErrorBOMMaterial(percentageFieldHighlightRed)}
								onChange={setMaterialPercentage}
								placeholderRes="FormatDoublePercentage"
								required
								selectOnFocus
								sx={{ width: 100, minWidth: 100 }}
								value={restrictedPercentage}
							/>
							<IconButton
								onClick={() => removeBOMMaterial(key)}
								size="small"
							>
								<CloseIcon />
							</IconButton>
						</FlexRow>
						<BaseCompositionMaterialList
							addBaseCompositionMaterial={addBaseCompositionMaterial}
							baseCompositionMaterials={baseCompositionMaterials}
							compositionMaterialsHaveDummy={compositionMaterialsHaveDummy}
							parent={materialBOM}
						/>
					</FlexColumn>
				);
			})}
			<Button
				design="link"
				disabled={compositionMaterialsHaveDummy || (fromStyleBom && !currentSelectedBOM)}
				onClick={addBOMMaterial}
				res="BOMMaterial"
				startIcon={<AddIcon />}
			/>
		</FlexColumn>
	);
});

export default BaseCompositionBOMMaterialList;
